/**
 * i18n — Enterprise Localization Setup
 *
 * Architecture:
 *   • ONE English master file (en.json) — the single source of truth
 *   • ALL other languages are translated on demand via the backend
 *   • Translations stored in database (TranslationCache + TranslationOverride)
 *   • Frontend memory cache for zero-latency repeat lookups
 *
 * This module creates the Vue I18n instance, exposes the English messages,
 * and provides helpers for locale switching and language detection.
 *
 * No static locale JSON files exist for non-English languages.
 */

import { createI18n, type I18nOptions } from "vue-i18n";
import en from "./en.json";

// ── Supported languages ───────────────────────────────────────────────────────
// Any ISO 639 code is accepted by the backend; this list controls what appears
// in UI pickers. Add entries here to surface new languages in the admin panel.

export const SUPPORTED_LOCALES = [
  // Global languages
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "nl", name: "Nederlands" },
  { code: "pt", name: "Português" },
  { code: "pl", name: "Polski" },
  { code: "sv", name: "Svenska" },
  { code: "fi", name: "Suomi" },
  { code: "da", name: "Dansk" },
  { code: "cs", name: "Čeština" },
  { code: "sk", name: "Slovenčina" },
  { code: "hu", name: "Magyar" },
  { code: "ro", name: "Română" },
  { code: "el", name: "Ελληνικά" },
  { code: "tr", name: "Türkçe" },
  { code: "zh", name: "中文" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "ru", name: "Русский" },
  { code: "ar", name: "العربية" },
  { code: "he", name: "עברית" },
  { code: "th", name: "ไทย" },
  { code: "vi", name: "Tiếng Việt" },
  { code: "id", name: "Bahasa Indonesia" },
  { code: "ms", name: "Bahasa Melayu" },
  // Indian languages
  { code: "hi", name: "हिन्दी" },
  { code: "bn", name: "বাংলা" },
  { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" },
  { code: "gu", name: "ગુજરાતી" },
  { code: "kn", name: "ಕನ್ನಡ" },
  { code: "ml", name: "മലയാളം" },
  { code: "mr", name: "मराठी" },
  { code: "ur", name: "اردو" },
  { code: "pa", name: "ਪੰਜਾਬੀ" },
  { code: "or", name: "ଓଡ଼ିଆ" },
  { code: "as", name: "অসমীয়া" },
  { code: "sd", name: "سنڌي" },
  { code: "kok", name: "कोंकणी" },
  { code: "mni", name: "মণিপুরী" },
  { code: "sa", name: "संस्कृतम्" },
  { code: "ne", name: "नेपाली" },
  { code: "brx", name: "बड़ो" },
  { code: "sat", name: "संताली" },
  { code: "doi", name: "डोगरी" },
  { code: "mai", name: "मैथिली" },
  { code: "ks", name: "कॉशुर" },
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]["code"];

// ── Vue I18n instance ─────────────────────────────────────────────────────────

const options = {
  legacy: false,
  locale: "en",
  fallbackLocale: "en",
  messages: { en },
  missingWarn: false,
  fallbackWarn: false,
} as const;

export const i18n = createI18n(options as unknown as I18nOptions);

// ── English messages export (used by useLocalization for batch pre-translate) ─
export const enMessages = en;

/**
 * Register translated messages for a locale.
 *
 * Called by useLocalization.setLocale() after batch-translating all en.json
 * values via the backend. This replaces the old lazy-load JSON approach.
 */
export function setLocaleMessages(
  locale: string,
  messages: Record<string, unknown>,
): void {
  i18n.global.setLocaleMessage(locale, messages);
  i18n.global.locale.value = locale;
}

/**
 * Initialise a locale with English messages as a base.
 *
 * Ensures that t(key) returns English text (never raw keys) while the
 * backend batch-translation is in flight.
 */
export function initLocaleWithEnglishBase(locale: string): void {
  i18n.global.setLocaleMessage(locale, { ...en });
  i18n.global.locale.value = locale;
}

/**
 * Resolve the best locale from the priority chain:
 *   1. User preference (from API / profile)
 *   2. Company default (Company.defaultLanguage)
 *   3. Browser locale (navigator.language)
 *   4. English fallback
 */
export function resolveBestLocale(
  userPreference?: string | null,
  companyDefault?: string | null,
): string {
  const codes = SUPPORTED_LOCALES.map((l) => l.code) as string[];

  if (userPreference && codes.includes(userPreference)) return userPreference;
  if (companyDefault && codes.includes(companyDefault)) return companyDefault;

  const browserLocale = navigator.language ?? "en";
  if (codes.includes(browserLocale)) return browserLocale;

  const langOnly = browserLocale.split("-")[0];
  if (codes.includes(langOnly)) return langOnly;

  return "en";
}
